/**
 * UI state management and DOM manipulation for CardElement
 */

import type { CardElementRenderer } from './renderer';

export class UIController {
  private shadowRoot: ShadowRoot;

  constructor(
    private renderer: CardElementRenderer
  ) {
    // Get shadow root from renderer
    this.shadowRoot = renderer.getShadowRoot();
  }

  /**
   * Show or hide error message for a specific field
   */
  showError(field: string, message: string | null): void {
    const errorEl = this.shadowRoot.querySelector(`#error-${field}`) as HTMLElement;
    if (errorEl) {
      errorEl.textContent = message || '';
      errorEl.style.display = message ? 'block' : 'none';
    }
  }

  /**
   * Show general error message
   */
  showGeneralError(message: string): void {
    // Hide success message if showing error
    this.hideGeneralSuccess();

    const errorEl = this.shadowRoot.querySelector('#general-error') as HTMLElement;
    const errorText = this.shadowRoot.querySelector('.inflowpay-general-error-text') as HTMLElement;
    if (errorEl && errorText) {
      errorText.textContent = message;
      errorEl.style.display = 'flex';
    }
  }

  /**
   * Show general success message
   */
  showGeneralSuccess(message: string): void {
    // Hide error message if showing success
    this.hideGeneralError();

    const successEl = this.shadowRoot.querySelector('#general-success') as HTMLElement;
    const successText = this.shadowRoot.querySelector('.inflowpay-general-success-text') as HTMLElement;
    if (successEl && successText) {
      successText.textContent = message;
      successEl.style.display = 'flex';
    }
  }

  /**
   * Hide general error message
   */
  hideGeneralError(): void {
    const generalError = this.shadowRoot.querySelector('#general-error') as HTMLElement;
    if (generalError) {
      generalError.style.display = 'none';
    }
  }

  /**
   * Hide general success message
   */
  hideGeneralSuccess(): void {
    const generalSuccess = this.shadowRoot.querySelector('#general-success') as HTMLElement;
    if (generalSuccess) {
      generalSuccess.style.display = 'none';
    }
  }

  /**
   * Clear all error messages
   */
  clearErrors(): void {
    this.showError('card-number', null);
    this.showError('expiry', null);
    this.showError('cvc', null);
    this.hideGeneralError();
    this.updateButtonState();
  }

  /**
   * Clear all messages (errors and success)
   */
  clearMessages(): void {
    this.clearErrors();
    this.hideGeneralSuccess();
  }

  /**
   * Set button loading state
   */
  setButtonLoading(loading: boolean): void {
    const button = this.shadowRoot.querySelector('#inflowpay-submit-btn') as HTMLButtonElement;
    const text = this.shadowRoot.querySelector('.inflowpay-button-text') as HTMLElement;
    const loader = this.shadowRoot.querySelector('.inflowpay-button-loader') as HTMLElement;

    if (button && text && loader) {
      button.disabled = loading;
      text.style.display = loading ? 'none' : 'inline';
      loader.style.display = loading ? 'inline-block' : 'none';
    }
  }

  /**
   * Update button disabled state based on field errors and completion
   * Disables button if any error div has content/is visible OR if fields are incomplete
   * Respects loading state - won't re-enable if button is in loading state
   */
  updateButtonState(): void {
    const button = this.shadowRoot.querySelector('#inflowpay-submit-btn') as HTMLButtonElement;
    if (!button) return;

    // Check if button is in loading state (don't override loading state)
    const loader = this.shadowRoot.querySelector('.inflowpay-button-loader') as HTMLElement;
    const isLoading = loader && loader.style.display !== 'none';
    if (isLoading) return;

    // Check if any error div has content and is visible
    const cardNumberError = this.shadowRoot.querySelector('#error-card-number') as HTMLElement;
    const expiryError = this.shadowRoot.querySelector('#error-expiry') as HTMLElement;
    const cvcError = this.shadowRoot.querySelector('#error-cvc') as HTMLElement;

    const hasErrors =
      (cardNumberError &&
        cardNumberError.style.display !== 'none' &&
        cardNumberError.textContent?.trim()) ||
      (expiryError &&
        expiryError.style.display !== 'none' &&
        expiryError.textContent?.trim()) ||
      (cvcError &&
        cvcError.style.display !== 'none' &&
        cvcError.textContent?.trim());

    // Check if all fields are filled
    const cardNumberInput = this.renderer.getInput('inflowpay-card-number');
    const expiryInput = this.renderer.getInput('inflowpay-expiry');
    const cvcInput = this.renderer.getInput('inflowpay-cvc');

    const cardNumber = cardNumberInput?.value.replace(/\s/g, '') || '';
    const expiry = expiryInput?.value || '';
    const cvc = cvcInput?.value || '';

    const isComplete =
      cardNumber.length > 0 && expiry.length > 0 && cvc.length > 0;

    // Disable button if there are errors OR if fields are incomplete
    button.disabled = !!hasErrors || !isComplete;
  }

  /**
   * Setup event listeners for inputs and button
   */
  setupEventListeners(
    onCardInput: (value: string) => void,
    onExpiryInput: (value: string) => void,
    onCvcInput: (value: string) => void,
    onSubmit: () => void
  ): void {
    const cardNumberInput = this.renderer.getInput('inflowpay-card-number');
    const expiryInput = this.renderer.getInput('inflowpay-expiry');
    const cvcInput = this.renderer.getInput('inflowpay-cvc');
    const button = this.renderer.getButton();

    // Track previous card number length to detect completion transition
    let previousCardLength = 0;

    /**
     * Card Number Input Handler
     * Formats as space-separated groups of 4 digits. Auto-advances to expiry when complete (16 digits).
     */
    cardNumberInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const value = target.value.replace(/\s/g, '');
      const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
      target.value = formatted;
      
      // Auto-advance to expiry when card number becomes complete
      if (value.length === 16 && previousCardLength < 16) {
        const expiryValue = expiryInput.value;
        const expiryIsEmpty = expiryValue.length === 0;
        const expiryIsIncomplete = expiryValue.length > 0 && expiryValue.length < 5;
        
        if (expiryIsEmpty || expiryIsIncomplete) {
          setTimeout(() => expiryInput.focus(), 50);
        }
      }
      
      previousCardLength = value.length;
      onCardInput(target.value);
      this.updateButtonState();
    });

    /**
     * Card Number Keydown Handler
     * Enter: Submit if valid. Number keys: Auto-advance to next incomplete field (expiry → CVC).
     */
    cardNumberInput.addEventListener('keydown', (e) => {
      // Enter: Submit payment
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!button.disabled) {
          onSubmit();
        }
        return;
      }
      
      // Auto-advance: When complete (16 digits), advance to next incomplete field
      if (/[0-9]/.test(e.key)) {
        const currentValue = cardNumberInput.value.replace(/\s/g, '');
        const cursorPos = cardNumberInput.selectionStart ?? 0;
        const isComplete = currentValue.length === 16;
        const cursorAtEnd = cursorPos >= cardNumberInput.value.length - 1;
        
        if (isComplete && cursorAtEnd) {
          const expiryValue = expiryInput.value;
          const cvcValue = cvcInput.value;
          const expiryIsEmpty = expiryValue.length === 0;
          const expiryIsIncomplete = expiryValue.length > 0 && expiryValue.length < 5;
          const cvcIsIncomplete = cvcValue.length > 0 && cvcValue.length < 3;
          const expiryIsComplete = expiryValue.length === 5;
          
          // Advance to expiry if incomplete
          if (expiryIsEmpty || expiryIsIncomplete) {
            expiryInput.focus();
            const expiryLength = expiryValue.length;
            expiryInput.setSelectionRange(expiryLength, expiryLength);
          } 
          // Advance to CVC if expiry complete but CVC incomplete
          else if (expiryIsComplete && cvcIsIncomplete) {
            cvcInput.focus();
            const cvcLength = cvcValue.length;
            cvcInput.setSelectionRange(cvcLength, cvcLength);
          }
        }
      }
    });

    cardNumberInput.addEventListener('keypress', (e) => {
      if (!/[0-9]/.test(e.key) && e.key !== ' ') {
        e.preventDefault();
      }
    });

    /**
     * Expiry Input Handler
     * Formats as MM/YY. Auto-advances to CVC when complete (5 chars).
     */
    expiryInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      let value = target.value.replace(/\D/g, '');
      if (value.length >= 2) {
        value = value.slice(0, 2) + '/' + value.slice(2, 4);
      }
      target.value = value;
      
      // Auto-advance to CVC when expiry complete
      if (value.length === 5) {
        const cvcValue = cvcInput.value;
        const cvcIsEmpty = cvcValue.length === 0;
        const cvcIsIncomplete = cvcValue.length > 0 && cvcValue.length < 3;
        
        if (cvcIsEmpty || cvcIsIncomplete) {
          setTimeout(() => cvcInput.focus(), 50);
        }
      }
      
      onExpiryInput(target.value);
      this.updateButtonState();
    });

    /**
     * Expiry Keydown Handler
     * Enter: Submit if valid. Backspace: Handle "/" deletion, reverse-advance if empty.
     * Number keys: Auto-advance to CVC when expiry complete.
     */
    expiryInput.addEventListener('keydown', (e) => {
      // Enter: Submit payment
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!button.disabled) {
          onSubmit();
        }
        return;
      }
      
      // Backspace: Handle "/" deletion or reverse-advance
      if (e.key === 'Backspace') {
        const target = e.target as HTMLInputElement;
        const cursorPos = target.selectionStart ?? 0;
        const value = target.value;
        
        // Delete "/" and preceding digit when cursor after "/"
        if (cursorPos === 3 && value.length >= 3 && value[2] === '/') {
          e.preventDefault();
          const digits = value.replace(/\D/g, '');
          if (digits.length > 0) {
            const newDigits = digits.slice(0, -1);
            target.value = newDigits.length >= 2 ? `${newDigits.slice(0, 2)}/${newDigits.slice(2, 4)}` : newDigits;
            target.setSelectionRange(2, 2);
            onExpiryInput(target.value);
            this.updateButtonState();
          }
          return;
        }
        
        // Reverse-advance to card number if empty
        if (cursorPos === 0 && value.length === 0) {
          e.preventDefault();
          setTimeout(() => cardNumberInput.focus(), 50);
          return;
        }
      }
      
      // Auto-advance to CVC when expiry complete
      if (/[0-9]/.test(e.key)) {
        const currentValue = expiryInput.value;
        const cursorPos = expiryInput.selectionStart ?? 0;
        const isComplete = currentValue.length === 5;
        const cursorAtEnd = cursorPos >= currentValue.length - 1;
        
        if (isComplete && cursorAtEnd) {
          const cvcValue = cvcInput.value;
          const cvcIsEmpty = cvcValue.length === 0;
          const cvcIsIncomplete = cvcValue.length > 0 && cvcValue.length < 3;
          
          if (cvcIsEmpty || cvcIsIncomplete) {
            cvcInput.focus();
            const cvcLength = cvcValue.length;
            cvcInput.setSelectionRange(cvcLength, cvcLength);
          }
        }
      }
    });

    /**
     * CVC Input Handler
     * Updates form state on input.
     */
    cvcInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const value = target.value;
      
      onCvcInput(value);
      this.updateButtonState();
    });

    cvcInput.addEventListener('keypress', (e) => {
      if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
      }
    });

    /**
     * CVC Keydown Handler
     * Enter: Submit if valid. Backspace: Reverse-advance to expiry if empty.
     */
    cvcInput.addEventListener('keydown', (e) => {
      // Enter: Submit payment
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!button.disabled) {
          onSubmit();
        }
        return;
      }
      
      // Reverse-advance to expiry if empty
      if (e.key === 'Backspace' && (e.target as HTMLInputElement).selectionStart === 0 && (e.target as HTMLInputElement).value.length === 0) {
        e.preventDefault();
        setTimeout(() => expiryInput.focus(), 50);
      }
    });

    // Button click handler
    button.addEventListener('click', () => {
      onSubmit();
    });
  }
}
